import { useEffect, useState } from "react";
import { APIProvider, Map, AdvancedMarker, useMap, Pin } from "@vis.gl/react-google-maps";
import { MdMyLocation } from "react-icons/md";
import ToolTipComponent from "@CubeComponents/html/tooltip";
import { getLocationCoordinates } from "@Services/utils/helpers";
import { DELHI_CORDINATES } from "@Services/constants";
import { useTranslation } from "react-i18next";

function GoogleMap({
  allowPanning = true,
  allowManualSelection = true,
  additionalMarker = null,
  defaultCenter = null,
  allowFullScreen = true,
  allowRecenterBtns = true,
  allowSearch = true,
  autoFetchLiveLocation = true,
  getLocationCallBack,
  zoomLevel = 14,
  fetchAddress = false,
  showLiveLocation = true,
}) {
  const [currentPosition, setCurrentPosition] = useState(defaultCenter);
  const [searchedPosition, setSearchedPosition] = useState(additionalMarker);
  // const [isLocationDenied, setIsLocationDenied] = useState(true);
  const [selectedPinAddress, setSelectedPinAddress] = useState({});
  const [mapZoomLevel, setMapZoomLevel] = useState(zoomLevel);
  const [isLoaded, setIsLoaded] = useState(false);
  const { t } = useTranslation();

  const RecenterBtn = () => {
    const map = useMap();

    useEffect(() => {
      if (zoomLevel) {
        if (map) {
          map.setZoom(zoomLevel);
        }
      }
    }, [zoomLevel]);

    const handleCurrentRecenter = () => {
      if (map && currentPosition) {
        map.panTo(currentPosition);
      }
    };
    const handleSelectedRecenter = () => {
      if (map && searchedPosition) {
        map.panTo(searchedPosition);
      }
    };

    useEffect(() => {
      handleSelectedRecenter();
    }, [searchedPosition]);

    map.addListener("dragend", () => {
      setMapZoomLevel(map.getZoom());
    });

    useEffect(() => {
      if (!map || !allowSearch) return;

      const placeAutocomplete = new window.google.maps.places.PlaceAutocompleteElement();
      placeAutocomplete.setAttribute("placeholder", "Search for a place");
      placeAutocomplete.id = "place-autocomplete-input";
      const card = document.createElement("div");
      card.id = "place-autocomplete-card";
      card.style.background = "white";
      card.style.borderRadius = "4px";
      card.style.borderColor = "white";
      card.appendChild(placeAutocomplete);

      map.controls[window.google.maps.ControlPosition.TOP_LEFT].push(card);
      placeAutocomplete.addEventListener("gmp-select", async ({ placePrediction }) => {
        const place = placePrediction.toPlace();
        await place.fetchFields({ fields: ["displayName", "formattedAddress", "location"] });
        if (place.viewport) {
          map.fitBounds(place.viewport);
        }
        const searchedLocation = { lat: place.location.lat(), lng: place.location.lng() };
        map.panTo(searchedLocation);
        setSearchedPosition(() => searchedLocation);
        fetchPinAddress(searchedLocation);
      });

      // Cleanup on unmount
      return () => {
        if (map.controls[window.google.maps.ControlPosition.TOP_LEFT].length) {
          map.controls[window.google.maps.ControlPosition.TOP_LEFT].pop();
        }
      };
    }, [map, currentPosition]);

    return allowRecenterBtns ? (
      <>
        <button
          onClick={handleSelectedRecenter}
          style={{
            position: "absolute",
            insetInlineEnd: "9px",
            bottom: "80px",
            zIndex: 999,
            padding: "8px 10px",
            cursor: "pointer",
            borderRadius: "50%",
            background: "white",
          }}
        >
          <ToolTipComponent content={t("Recenter to selected location")} position="bottom">
            <MdMyLocation size={24} color="#FBBC04" />
          </ToolTipComponent>
        </button>
        {showLiveLocation && (
          <button
            onClick={handleCurrentRecenter}
            style={{
              position: "absolute",
              insetInlineEnd: "9px",
              bottom: "130px",
              zIndex: 999,
              padding: "8px 10px",
              cursor: "pointer",
              borderRadius: "50%",
              background: "white",
            }}
          >
            <ToolTipComponent content={t("Recenter to live location")} position="top">
              <MdMyLocation size={24} color="#EA4335" />
            </ToolTipComponent>
          </button>
        )}
      </>
    ) : (
      ""
    );
  };

  const fetchPinAddress = (latLng) => {
    if (!window.google?.maps?.Geocoder) return;
    const geocoder = new window.google.maps.Geocoder();

    geocoder.geocode({ location: latLng }, (results, status) => {
      if (status === "OK" && results[0]) {
        const components = results[0].address_components;

        const getComponent = (type) => components.find((c) => c.types.includes(type))?.long_name || "";

        const street = getComponent("route");
        const streetNumber = getComponent("street_number");
        const locality = getComponent("locality");
        const postalCode = getComponent("postal_code");
        const state = getComponent("administrative_area_level_1");
        const country = getComponent("country");

        const fullAddress = results[0].formatted_address;

        const structuredAddress = {
          fullAddress,
          street: `${streetNumber} ${street}`.trim(),
          city: locality,
          state,
          country,
          postalCode,
        };
        setSelectedPinAddress(structuredAddress);
      }
    });
  };

  useEffect(() => {
    if (additionalMarker) {
      setSearchedPosition(additionalMarker);
    }
  }, [additionalMarker]);

  useEffect(() => {
    if (defaultCenter) {
      setCurrentPosition(defaultCenter);
    }
  }, [defaultCenter]);

  useEffect(() => {
    if (searchedPosition && fetchAddress && isLoaded) {
      fetchPinAddress(searchedPosition);
    }
  }, [searchedPosition, isLoaded]);

  useEffect(() => {
    if (getLocationCallBack) {
      getLocationCallBack({ current: currentPosition, selected: searchedPosition, selectedPinAddress, mapZoomLevel });
    }
  }, [searchedPosition, currentPosition, selectedPinAddress, mapZoomLevel]);

  useEffect(() => {
    updatePositionWithCurrentLocation();
  }, []);

  const updatePositionWithCurrentLocation = async () => {
    if (autoFetchLiveLocation) {
      const currentLocation = await getLocationCoordinates();
      const location = {
        lat: currentLocation[0],
        lng: currentLocation[1],
      };
      if (showLiveLocation) {
        setCurrentPosition(location);
      }
      if (!additionalMarker) {
        setSearchedPosition(location);
        fetchPinAddress(location);
      }
    } else if (!additionalMarker) {
      setSearchedPosition(DELHI_CORDINATES);
      fetchPinAddress(DELHI_CORDINATES);
    }
  };

  const handlePointClick = (args) => {
    if (allowManualSelection) {
      setSearchedPosition(args.detail.latLng);
      fetchPinAddress(args.detail.latLng);
    }
  };

  return currentPosition || searchedPosition ? (
    <APIProvider onLoad={() => setIsLoaded(true)} apiKey={process.env.REACT_APP_GOOGLE_KEY} libraries={["places"]}>
      <Map
        onClick={handlePointClick}
        mapId="d1b0b06a3bb418a339d8e0f6"
        defaultCenter={searchedPosition || currentPosition}
        defaultZoom={mapZoomLevel}
        options={{
          streetViewControl: false,
          disableDefaultUI: !allowPanning,
          mapTypeControl: false,
          disableDoubleClickZoom: true,
          draggable: allowPanning,
          fullscreenControl: allowFullScreen,
        }}
      >
        <RecenterBtn />
        {showLiveLocation && isLoaded && <AdvancedMarker position={currentPosition} />}
        {searchedPosition && isLoaded && (
          <AdvancedMarker position={searchedPosition}>
            <Pin background={"#FBBC04"} glyphColor={"#000"} borderColor={"#000"} />
          </AdvancedMarker>
        )}
      </Map>
    </APIProvider>
  ) : (
    <div>Map Loading...</div>
  );
}
export default GoogleMap;
